// eslint-disable-next-line no-unused-vars
import React, { CSSProperties, useEffect, useRef } from "react";
import XRipple from "./XRipple";
import { useState } from "react";
import _ from "lodash";
import XText from "./XText";
import { ColorX, ZArray, ZFunc } from "../STATIC";
import XTooltip from "./XTooltip";
import ActivityIndicator from "./ActivityIndicator";
import HStack from "./HStack";
import { SysConfig } from "__SYSDefault/Config";

/**
 * @typedef {import("./XTooltip").xtooltipparams} xtooltipparams
 * 
 * @typedef {Object} xbtndivparams
 * @property {CSSProperties} style
 * @property {any} children
 * @property {Number | String} width
 * @property {Number | String} height
 * @property {Number | String} minWidth
 * @property {Number | String} maxWidth
 * @property {Number | String} minHeight
 * @property {Number | String} maxHeight
 * @property {Number | [Number, Number, Number, Number]} p
 * @property {Number} pH
 * @property {Number} pV
 * @property {Number} pT
 * @property {Number} pB
 * @property {Number} pL
 * @property {Number} pR
 * @property {Number | [Number, Number, Number, Number]} m
 * @property {Number} mH
 * @property {Number} mV
 * @property {Number} mT
 * @property {Number} mB
 * @property {Number} mL
 * @property {Number} mR
 * @property {Number | [Number, Number, Number, Number]} bW
 * @property {"none" | "dotted" | "inset" | "solid" | "dashed" 
 *  | "groove" | "hidden" | "double" | "ridge" | "outset"} bStyle
 * @property {Number | [Number, Number, Number, Number]} round
 * @property {Boolean} shadow
 * @property {Number} shadowSize
 * @property {String} shadowColor
 * @property {Boolean} fullWidth
 * @property {Boolean} fullHeight
 * @property {Boolean} fitWidthToContent
 * @property {Boolean} fitHeightToContent
 * @property {Boolean} autoWidth
 * @property {Boolean} autoHeight
 * @property {import("react").MutableRefObject} forwardRef
 * @property {"absolute" | "relative" | "static" | "fixed" | "sticky"} position
 * @property {"hidden" | "visible" | "scroll" | "auto"} overflow
 * @property {"static" | "block" | "flex" | "inline-block" | "none" | "grid"} display
 * @property {Number} zIndex
 * @property {Boolean} flex
 * @property {Number} flexShrink
 * @property {Number} flexGrow
 * @property {Number | String} flexBasis
 * @property {"nowrap" | "wrap" | "wrap-reverse"} flexWrap
 * @property {Number} opacity
 * @property {Boolean} colorTransition
 * @property {Boolean} sizeTransition
 * @property {Boolean} positionTransition
 * @property {Boolean} allTransition
 * @property {Number} transitionDuration
 * @property {Number | String} lockWidth
 * @property {Number | String} lockHeight
 * @property {"auto" | "default" | "none" 
 * | "context-menu" | "help" | "pointer" | "progress" | "wait"
 * | "cell" | "crosshair" | "text" | "vertical-text" | "alias" 
 * | "copy" | "move" | "no-drop" | "not-allowed" | "grab" | "grabbing"
 * | "all-scroll" | "col-resize" | "row-resize" | "n-resize" | "e-resize"
 * | "s-resize" | "w-resize" | "ne-resize" | "nw-resize" | "se-resize"
 * | "sw-resize" | "ew-resize" | "ns-resize" | "nesw-resize" | "nwse-resize"
 * | "zoom-in" | "zoom-out"} cursor
 * @property {Boolean} userSelectNone
 * @property {Number | String} top
 * @property {Number | String} left
 * @property {Number | String} right
 * @property {Number | String} bottom
 * 
 * @typedef {Object} xbutton
 * @property {Boolean} disabled
 * @property {Number} disableOpacity
 * @property {CSSProperties} hoverStyle
 * @property {Function} onClick
 * @property {Function} onPress
 * @property {Function} onLongPress
 * @property {Function} onMiddleClick
 * @property {Function} onRightClick
 * @property {Function} onHover
 * @property {Function} onHoverOff
 * @property {String} rippleColor
 * @property {String} color
 * @property {String} bColor
 * @property {String} bgColor
 * @property {String} hoverColor
 * @property {String} hoverBgColor
 * @property {String} disableColor
 * @property {String} disableBgColor
 * @property {Number | String} padding
 * @property {String} tooltip
 * @property {(rect) => {left, top}} tooltipPos
 * @property {Number} tooltipWidth
 * @property {"darken" | "invert" | "none"} hoverMode
 * @property {"greyOut" | "none"} disableMode
 * @property {Number} delayAfterPress
 * @property {Number} longPressDuration
 * @property {Boolean} showLoading
 * @property {Boolean} notInjectColor
 * @property {"auto" | "flex-start" | "flex-end" | "center" | "baseline" | "stretch"} alignSelf
 * 
 * @typedef {xbutton & xtooltipparams & xbtndivparams} xbuttonparams
 * 
 * @param {xbuttonparams} param0
 **/
function XButton ({
  children, 
  width = "auto",
  height = "auto",
  minWidth = undefined,
  maxWidth = undefined,
  minHeight = undefined,
  maxHeight = undefined, 
  disabled = false,
  disableOpacity = 1,
  style = {},
  hoverStyle = {},
  onClick,
  onPress,
  onLongPress,
  onMiddleClick,
  onRightClick,
  onHover,
  onHoverOff,
  rippleColor = ColorX.CSS("pureDark", 0.5),
  bColor = undefined,
  bgColor = "transparent",
  hoverColor = undefined,
  hoverBgColor = undefined,
  disableColor = ColorX.CSS("lightGrey"),
  disableBgColor = undefined,
  color = ColorX.CSS("pureDark"),
  padding = 10,
  tooltip = "",
  tooltipPos = (rect) => ({
    left: (rect?.x + rect?.width / 2) ?? 0,
    top: (rect?.y + rect?.height) ?? 0
  }),
  tooltipWidth = 200,
  hoverMode = "darken",
  disableMode = "greyOut",
  p = undefined,
  pH = undefined,
  pV = undefined,
  pT = undefined,
  pB = undefined,
  pL = undefined,
  pR = undefined,
  m = undefined,
  mH = undefined,
  mV = undefined,
  mT = undefined,
  mB = undefined,
  mL = undefined,
  mR = undefined,
  bW = undefined,
  bStyle = "solid",
  round = 5,
  shadow = undefined,
  shadowSize = 1,
  shadowColor = ColorX.CSS("greyOut", 0.2),
  fullWidth = undefined,
  fullHeight = undefined,
  fitWidthToContent = undefined,
  fitHeightToContent = undefined,
  autoWidth = undefined,
  autoHeight = undefined,
  delayAfterPress = SysConfig.delayAfterPress,
  longPressDuration = SysConfig.longPressDuration,
  showLoading = SysConfig.showLoadingButton,
  forwardRef = undefined,
  position = undefined,
  overflow = undefined,
  display = undefined,
  zIndex = undefined,
  flex = undefined,
  flexShrink = undefined,
  flexGrow = undefined,
  flexWrap = undefined,
  flexBasis = undefined,
  opacity = undefined,
  colorTransition = false,
  sizeTransition = false,
  positionTransition = false,
  allTransition = false,
  transitionDuration = 500,
  notInjectColor = false,
  lockWidth = undefined,
  lockHeight = undefined,
  cursor = undefined,
  userSelectNone = false,
  top = undefined,
  left = undefined,
  right = undefined,
  bottom = undefined,
  alignSelf = undefined
}){
  const [hover, setHover] = useState(false);
  const [tempDisable, setTempDisable] = useState(false);
  const tempTimeout = useRef(null);

  const onMouseEnter = () => {
    onHover?.();
    setHover(true);
  };

  const onMouseLeave = () => {
    onHoverOff?.();
    setHover(false);
    if(tempTimeout?.current) {
      clearTimeout(tempTimeout.current);
      tempTimeout.current = null;
    }
  };

  const _onMouseDown = (e) => {
    if(tempDisable || disabled) return;
    if(!onLongPress){
      _redirect(e, false);
      setTempDisable(true);
    }else{
      tempTimeout.current = setTimeout(onLongPress, longPressDuration);
    }
  };

  const _redirect = (e, long = false) => {
    switch(e.button){
      case 0: 
        if(long) {
          onLongPress?.(e) ?? onPress?.(e) ?? onClick?.(e); 
        }else{
          onPress?.(e) ?? onClick?.(e);
        }
        break;
      case 1: onMiddleClick?.(e); break;
      case 2: onRightClick?.(e); break;
      default: break;
    }
  };

  const _onMouseUp = (e) => {
    if(tempDisable || disabled) return;
    
    if(onLongPress){
      if(tempTimeout?.current) {
        clearTimeout(tempTimeout.current);
        tempTimeout.current = null;
      }
      _redirect(e, false);
      setTempDisable(true);
    }
  };

  useEffect(() => {
    const timeout = setTimeout(release, delayAfterPress);
    return () => clearTimeout(timeout);
  }, [tempDisable, delayAfterPress]);

  const release = () => {
    setTempDisable(false);
  };

  function renderChildren(_c){
    let _hoverColor = hoverColor;
    if(_hoverColor === undefined){
      switch(hoverMode){
        default: case "darken": _hoverColor = color; break;
        case "invert": _hoverColor = bgColor; break;
        case "none": _hoverColor = color; break;
      }
    }

    
    let _disableColor = disableColor;
    if(_disableColor === undefined){
      switch(disableMode){
        default: 
        case "none": case "darken": case "greyOut": 
          _disableColor = color; break;
      }
    }

    if(React.isValidElement(_c)){
      if(_.isString(_c.props.children)){
        return _c;
      }
      if(_c.props.children){
        let childs = ZArray.Ensure(_c.props.children);
        let newChildren = [];
        _.map(childs, (o, i) => {     
          let ___color = notInjectColor ? o?.props?.color : 
            (hover & !disabled ? _hoverColor : (disabled ? disableColor : color));
          if(React.isValidElement(o)){
            newChildren.push(
              React.cloneElement(o, {
                key: i,
                style: {
                  color: ___color,
                    ...(o?.props?.style ?? {})
                }
              })
            );
          }
        });
        return React.cloneElement(_c, {
          children: newChildren
        });
      }
      return _c;
    }else{
      return _.map(_c, (o, i) => {
        if(!o){
          return null;
        }else if(_.isArray(o)){
          return _.map(o, (v, x) => {
            return renderChildren(v);
          });
        }else if(_.isString(o)){
          return <XText key={i}>{o}</XText>;
        }else if(!React.isValidElement(o)){
          console.log(o);
          //return o;
        }else{
          let ___color = notInjectColor ? o?.props?.color : 
            (hover & !disabled ? _hoverColor : (disabled ? disableColor : color));
          return React.cloneElement(o, 
            {
              ...o.props, 
              key: i, 
              style: {
                color: ___color,
                opacity: disabled ? disableOpacity : 1, 
                ...o.props.style
              }
            });
        }
      });
    }
  }

  let _hoverBgColor = hoverBgColor;
  if(_hoverBgColor === undefined){
    switch(hoverMode){
      default: case "darken": _hoverBgColor = (bgColor === "transparent"? ColorX.CSS("greyOut", 0.1) : bgColor); break;
      case "invert": _hoverBgColor = color; break;
      case "none":  _hoverBgColor = bgColor; break;
    }
  }

  let _disableBgColor = disableBgColor;
  if(_disableBgColor === undefined){
    switch(disableMode){
      default: case "none": case "darken": case "invert": 
        _disableBgColor = bgColor; break;
    }
  }

  //Quick Access
  let _addStyle = {};
  _addStyle.boxSizing = "border-box";
  if(p !== undefined){
    if(_.isArray(p)){
      _addStyle.paddingTop = p[0] ?? 0;
      _addStyle.paddingRight = p[1] ?? 0;
      _addStyle.paddingBottom = p[2] ?? 0;
      _addStyle.paddingLeft = p[3] ?? 0;
    }else{
      _addStyle.padding = p;
    }
  }
  if(pH !== undefined) _addStyle.paddingLeft = _addStyle.paddingRight = pH;
  if(pV !== undefined) _addStyle.paddingTop = _addStyle.paddingBottom = pV;
  if(pT !== undefined) _addStyle.paddingTop = pT;
  if(pB !== undefined) _addStyle.paddingBottom = pB;
  if(pL !== undefined) _addStyle.paddingLeft = pL;
  if(pR !== undefined) _addStyle.paddingRight = pR;
  if(m !== undefined){
      if(_.isArray(m)){
        _addStyle.marginTop = m[0] ?? 0;
        _addStyle.marginRight = m[1] ?? 0;
        _addStyle.marginBottom = m[2] ?? 0;
        _addStyle.marginLeft = m[3] ?? 0;
      }else{
        _addStyle.margin = m;
      }
    }
  if(mH !== undefined) _addStyle.marginLeft = _addStyle.marginRight = mH;
  if(mV !== undefined) _addStyle.marginTop = _addStyle.marginBottom = mV;
  if(mT !== undefined) _addStyle.marginTop = mT;
  if(mB !== undefined) _addStyle.marginBottom = mB;
  if(mL !== undefined) _addStyle.marginLeft = mL;
  if(mR !== undefined) _addStyle.marginRight = mR;
  if(bW !== undefined) {
    if(_.isArray(bW)){
      _addStyle.borderTopWidth  = bW[0] ?? 0;
      if(bW[0] > 0) _addStyle.borderTopStyle = bStyle;
      _addStyle.borderRightWidth = bW[1] ?? 0;
      if(bW[1] > 0) _addStyle.borderRightStyle = bStyle;
      _addStyle.borderBottomWidth = bW[2] ?? 0;
      if(bW[2] > 0) _addStyle.borderBottomStyle = bStyle;
      _addStyle.borderLeftWidth = bW[3] ?? 0;
      if(bW[3] > 0) _addStyle.borderLeftStyle = bStyle;
    }else{
      _addStyle.borderWidth = bW;
      if(bW > 0) _addStyle.borderStyle = bStyle;
    }
  }
  if(round !== undefined) {
    if(_.isArray(round)){
      _addStyle.borderTopLeftRadius = round[0] ?? 0;
      _addStyle.borderTopRightRadius = round[1] ?? 0;
      _addStyle.borderBottomLeftRadius = round[2] ?? 0;
      _addStyle.borderBottomRightRadius = round[3] ?? 0;
    }else{
      _addStyle.borderRadius = round;
    }
  }
  if(shadow !== undefined) _addStyle.boxShadow = `1px 1px ${shadowSize}px ${shadowSize}px ${shadowColor}`;
  if(fullWidth) _addStyle.width = "100%";
  if(fullHeight) _addStyle.height = "100%";
  if(fitWidthToContent) _addStyle.width = "fit-content";
  if(fitHeightToContent) _addStyle.height = "fit-content";
  if(autoWidth) _addStyle.width = "auto";
  if(autoHeight) _addStyle.height = "auto";
  if(position !== undefined) _addStyle.position = position;
  if(overflow !== undefined) _addStyle.overflow = overflow;
  if(display !== undefined) _addStyle.display = display;
  if(zIndex !== undefined) _addStyle.zIndex = zIndex;
  if(flex){
    _addStyle.display = "flex";
    _addStyle.flexGrow = 1;
    _addStyle.flexShrink = 1;
  }
  if(flexShrink !== undefined) _addStyle.flexShrink = flexShrink;
  if(flexGrow !== undefined) _addStyle.flexGrow = flexGrow;
  if(flexWrap !== undefined) _addStyle.flexWrap = flexWrap;
  if(flexBasis !== undefined) _addStyle.flexBasis = flexBasis;
  if(opacity !== undefined) _addStyle.opacity = opacity;
  if(minWidth !== undefined) _addStyle.minWidth = minWidth;
  if(maxWidth !== undefined) _addStyle.maxWidth = maxWidth;
  if(minHeight !== undefined) _addStyle.minHeight = minHeight;
  if(maxHeight !== undefined) _addStyle.maxHeight = maxHeight;
  if(cursor) _addStyle.cursor = cursor;
  if(userSelectNone) _addStyle.userSelect = "none";
  if(alignSelf) _addStyle.alignSelf = alignSelf;

  if(top !== undefined) _addStyle.top = top;
  if(left !== undefined) _addStyle.left = left;
  if(right !== undefined) _addStyle.right = right;
  if(bottom !== undefined) _addStyle.bottom = bottom;

  if(lockHeight !== undefined){
    _addStyle.height = lockHeight;
    _addStyle.minHeight = lockHeight;
    _addStyle.maxHeight = lockHeight;
  }

  if(lockWidth !== undefined){
    _addStyle.width = lockWidth;
    _addStyle.minWidth = lockWidth;
    _addStyle.maxWidth = lockWidth;
  }

  let transition = style?.transition?.split(",") ?? [];
  if(colorTransition){
    transition.push(`background-color ${transitionDuration}ms`);
    transition.push(`color ${transitionDuration}ms`);
    transition.push(`border-color ${transitionDuration}ms`);
    transition.push(`opacity ${transitionDuration}ms`);
  }else{
    if(color?.day && color?.night){
      transition.push(`color ${transitionDuration}ms`);
    }
    if(bgColor?.day && bgColor?.night){
      transition.push(`background-color ${transitionDuration}ms`);
    }
    if(bColor?.day && bColor?.night){
      transition.push(`border-color ${transitionDuration}ms`);
    }
  }

  if(positionTransition){
    transition.push(`top ${transitionDuration}ms`);
    transition.push(`left ${transitionDuration}ms`);
    transition.push(`right ${transitionDuration}ms`);
    transition.push(`bottom ${transitionDuration}ms`);
  }
  if(sizeTransition){
    transition.push(`width ${transitionDuration}ms`);
    transition.push(`height ${transitionDuration}ms`);
    transition.push(`min-width ${transitionDuration}ms`);
    transition.push(`min-height ${transitionDuration}ms`);
    transition.push(`max-width ${transitionDuration}ms`);
    transition.push(`max-height ${transitionDuration}ms`);
  }
  if(allTransition){
    transition.push(`all ${transitionDuration}ms`);
  }
  if(transition?.length > 0){
    _addStyle.transition = transition.join(", ");
  }

  let _filter = undefined;
  if(hover && hoverMode === "darken"){
    _filter = "brightness(95%)";
  }

  if(!notInjectColor && disabled && disableMode === "greyOut" && !disableBgColor){
    _filter = "grayscale(100%)";
  }

  let _finalBgColor = hover & !disabled ? _hoverBgColor : (disabled? _disableBgColor : bgColor);

  let _cursor = "pointer";
  if(tempDisable){_cursor = "wait"; }
  else if(disabled){_cursor = "not-allowed"; }

  let _style = {
    cursor: _cursor,
    width: width,
    height: height,
    padding: padding,
    backgroundColor: _finalBgColor,
    transition: "background 0.3s",
    borderWidth: 0,
    borderColor: bColor ?? bgColor,
    borderStyle: "solid",
    opacity: disabled ? disableOpacity : 1, 
    display: "flex",
    flexDirection: "column",
    alignItems: "center",
    justifyContent: "center",
    overflow: "hidden",
    filter: _filter,
    ...style,
    ..._addStyle,
    ...(hover ? hoverStyle : {}),
  };

  children = ZFunc.IfFuncExec(children, hover);

  const core = (_s) => (
    <XRipple color={rippleColor ?? hoverBgColor ?? bgColor} 
      onMouseDown={_onMouseDown} 
      onMouseUp={_onMouseUp}
      onMouseEnter={onMouseEnter} 
      onMouseLeave={onMouseLeave}
      enabled={!disabled}
      forwardRef={forwardRef}
      style={_s}>      
        {showLoading && tempDisable && 
          <HStack position="absolute" 
            top={0} left={0} zIndex={2} 
            background={ColorX.CSS("pureDark", 0.1)} height="100%">
            <ActivityIndicator loading={true}/>
          </HStack>
        }
        {renderChildren(children)}
    </XRipple>
  );

  if(_.isEmpty(tooltip)){
    return core(_style);
  }

  return (
    <XTooltip tooltip={tooltip} tooltipPos={tooltipPos} width={tooltipWidth}
      style={{
        ..._style
      }}>
      {core({
        width: "100%",
        height: "100%",
        borderRadius: _style?.borderRadius,
      })}
    </XTooltip>
  );

}

export default XButton;